import type {
  Block,
  TemplateContent,
  TemplateDefaults,
  TemplateSettings,
  UiTheme,
  ViewportSize,
} from "@aswin.dev/types";
import {
  createDefaultTemplateContent,
  normalizeTemplateContentPages,
} from "@aswin.dev/types";
import { sanitizeBlockUpdates } from "./block-tree";
import {
  computed,
  reactive,
  readonly,
  type DeepReadonly,
  type Ref,
} from "@vue/reactivity";

export interface EditorState {
  content: TemplateContent;
  selectedBlockId: string | null;
  viewport: ViewportSize;
  darkMode: boolean;
  previewMode: boolean;
  isDirty: boolean;
  uiTheme: UiTheme;
}

export interface UseEditorOptions {
  content: TemplateContent;
  defaultFontFamily?: string;
  templateDefaults?: TemplateDefaults;
  lockedBlocks?: Ref<Map<string, unknown>>;
}

export interface UseEditorReturn {
  state: DeepReadonly<EditorState>;
  content: Ref<TemplateContent>;
  selectedBlock: Ref<Block | null>;
  setContent: (content: TemplateContent, markDirty?: boolean) => void;
  selectBlock: (blockId: string | null) => void;
  setViewport: (viewport: ViewportSize) => void;
  setDarkMode: (darkMode: boolean) => void;
  setPreviewMode: (previewMode: boolean) => void;
  setUiTheme: (theme: UiTheme) => void;
  updateBlock: (blockId: string, updates: Partial<Block>) => void;
  updateSettings: (updates: Partial<TemplateSettings>) => void;
  addBlock: (
    block: Block,
    targetSectionId?: string,
    columnIndex?: number,
    index?: number,
  ) => void;
  removeBlock: (blockId: string) => void;
  moveBlock: (
    blockId: string,
    newIndex: number,
    targetSectionId?: string,
    columnIndex?: number,
  ) => void;
  isBlockLocked: (blockId: string) => boolean;
  markDirty: () => void;
  /** Wrap existing blocks as step 1 (popup / wizard canvases). No-op if already set. */
  ensureCanvasPages: () => void;
  /** Switch active canvas step; clears selection. */
  switchCanvasPage: (pageId: string) => void;
  /** Append an empty step and activate it. */
  addCanvasPage: () => void;
  /** Remove a step (must keep at least one). If active is removed, selects another. */
  removeCanvasPage: (pageId: string) => void;
  /** Update a step title shown in the canvas strip. */
  renameCanvasPage: (pageId: string, title: string) => void;
  /** Activate the next canvas step when using multi-page mode; returns false if none. */
  switchToNextCanvasPage: () => boolean;
  switchToPreviousCanvasPage: () => boolean;
}

export function useEditor(options: UseEditorOptions): UseEditorReturn {
  const initialRaw =
    options.content ??
    createDefaultTemplateContent(
      options.defaultFontFamily,
      options.templateDefaults,
    );
  const state = reactive<EditorState>({
    content: normalizeTemplateContentPages(initialRaw),
    selectedBlockId: null,
    viewport: "desktop",
    darkMode: false,
    previewMode: false,
    isDirty: false,
    uiTheme: "auto",
  });

  const content = computed({
    get: () => state.content,
    set: (value: TemplateContent) => {
      state.content = normalizeTemplateContentPages(value);
      state.isDirty = true;
    },
  });

  const selectedBlock = computed(() => {
    if (!state.selectedBlockId) return null;
    return findBlockById(state.content.blocks, state.selectedBlockId);
  });

  function findBlockById(blocks: Block[], id: string): Block | null {
    for (const block of blocks) {
      if (block.id === id) return block;
      if (block.type === "section") {
        for (const column of block.children) {
          const found = findBlockById(column, id);
          if (found) return found;
        }
      }
    }
    return null;
  }

  function findBlockParent(
    blocks: Block[],
    id: string,
    parent: {
      blocks: Block[];
      sectionId?: string;
      columnIndex?: number;
    } = { blocks },
  ): { blocks: Block[]; sectionId?: string; columnIndex?: number } | null {
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      if (block.id === id) return parent;
      if (block.type === "section") {
        for (let colIdx = 0; colIdx < block.children.length; colIdx++) {
          const result = findBlockParent(block.children[colIdx], id, {
            blocks: block.children[colIdx],
            sectionId: block.id,
            columnIndex: colIdx,
          });
          if (result) return result;
        }
      }
    }
    return null;
  }

  function isBlockLocked(blockId: string): boolean {
    return options.lockedBlocks?.value.has(blockId) ?? false;
  }

  // TODO(collab): the lock checks in addBlock/moveBlock/removeBlock/updateBlock
  // are shallow — they only consider the directly-targeted block id. A section
  // can still be removed, moved, or have its `children` array rewritten while a
  // peer is editing one of its descendants, which silently disrupts that peer's
  // edit. Add a `hasLockedDescendant(blockId)` helper and gate section-level
  // operations on it (and on the parent of each affected child) so cascades
  // through the tree are also blocked.

  function setContent(newContent: TemplateContent, markDirty = true): void {
    state.content = normalizeTemplateContentPages(newContent);
    if (markDirty) {
      state.isDirty = true;
    }
  }

  function selectBlock(blockId: string | null): void {
    if (blockId && isBlockLocked(blockId)) {
      return;
    }
    state.selectedBlockId = blockId;
  }

  function setViewport(viewport: ViewportSize): void {
    state.viewport = viewport;
  }

  function setDarkMode(darkMode: boolean): void {
    state.darkMode = darkMode;
  }

  function setUiTheme(theme: UiTheme): void {
    state.uiTheme = theme;
  }

  function setPreviewMode(previewMode: boolean): void {
    state.previewMode = previewMode;
    if (previewMode) {
      state.selectedBlockId = null;
    }
  }

  function updateBlock(blockId: string, updates: Partial<Block>): void {
    if (isBlockLocked(blockId)) {
      return;
    }
    const block = findBlockById(state.content.blocks, blockId);
    if (block) {
      Object.assign(block, sanitizeBlockUpdates(updates));
      state.isDirty = true;
    }
  }

  function updateSettings(updates: Partial<TemplateSettings>): void {
    state.content.settings = { ...state.content.settings, ...updates };
    state.isDirty = true;
  }

  function addBlock(
    block: Block,
    targetSectionId?: string,
    columnIndex = 0,
    index?: number,
  ): void {
    if (targetSectionId) {
      if (isBlockLocked(targetSectionId)) {
        return;
      }
      const section = findBlockById(state.content.blocks, targetSectionId);
      if (section && section.type === "section") {
        section.children[columnIndex] = section.children[columnIndex] || [];
        const targetArray = section.children[columnIndex];
        if (index !== undefined && index < targetArray.length) {
          targetArray.splice(index, 0, block);
        } else {
          targetArray.push(block);
        }
      }
    } else {
      if (index !== undefined && index < state.content.blocks.length) {
        state.content.blocks.splice(index, 0, block);
      } else {
        state.content.blocks.push(block);
      }
    }
    state.isDirty = true;
  }

  function removeBlock(blockId: string): void {
    if (isBlockLocked(blockId)) {
      return;
    }
    const parent = findBlockParent(state.content.blocks, blockId);
    if (parent) {
      const index = parent.blocks.findIndex((b) => b.id === blockId);
      if (index !== -1) {
        parent.blocks.splice(index, 1);
        if (state.selectedBlockId === blockId) {
          state.selectedBlockId = null;
        }
        state.isDirty = true;
      }
    }
  }

  function moveBlock(
    blockId: string,
    newIndex: number,
    targetSectionId?: string,
    columnIndex = 0,
  ): void {
    if (isBlockLocked(blockId)) {
      return;
    }
    if (targetSectionId && isBlockLocked(targetSectionId)) {
      return;
    }

    const parent = findBlockParent(state.content.blocks, blockId);
    if (!parent) return;

    const oldIndex = parent.blocks.findIndex((b) => b.id === blockId);
    if (oldIndex === -1) return;

    // Resolve target before mutating the source — otherwise an invalid
    // targetSectionId leaves the block spliced-out and unrecoverable.
    let targetArray: Block[];
    if (targetSectionId) {
      const section = findBlockById(state.content.blocks, targetSectionId);
      if (!section || section.type !== "section") return;
      section.children[columnIndex] = section.children[columnIndex] || [];
      targetArray = section.children[columnIndex];
    } else {
      targetArray = state.content.blocks;
    }

    const [block] = parent.blocks.splice(oldIndex, 1);
    targetArray.splice(newIndex, 0, block);

    state.isDirty = true;
  }

  function markDirty(): void {
    state.isDirty = true;
  }

  function ensureCanvasPages(): void {
    if (state.content.canvasPages?.length) {
      return;
    }
    const id = crypto.randomUUID();
    const blocks = state.content.blocks;
    state.content.canvasPages = [{ id, title: "Step 1", blocks }];
    state.content.activeCanvasPageId = id;
    state.content.blocks = blocks;
    state.isDirty = true;
  }

  function switchCanvasPage(pageId: string): void {
    const pages = state.content.canvasPages;
    if (!pages?.length) {
      return;
    }
    const page = pages.find((p) => p.id === pageId);
    if (!page) {
      return;
    }
    state.selectedBlockId = null;
    state.content.blocks = page.blocks;
    state.content.activeCanvasPageId = pageId;
    state.isDirty = true;
  }

  function addCanvasPage(): void {
    ensureCanvasPages();
    const pages = state.content.canvasPages!;
    const id = crypto.randomUUID();
    const title = `Step ${pages.length + 1}`;
    const newBlocks: Block[] = [];
    pages.push({ id, title, blocks: newBlocks });
    switchCanvasPage(id);
  }

  function removeCanvasPage(pageId: string): void {
    const pages = state.content.canvasPages;
    if (!pages || pages.length <= 1) {
      return;
    }
    const idx = pages.findIndex((p) => p.id === pageId);
    if (idx === -1) {
      return;
    }
    pages.splice(idx, 1);
    if (state.content.activeCanvasPageId === pageId) {
      const next = pages[Math.min(idx, pages.length - 1)];
      switchCanvasPage(next.id);
    } else {
      state.content.canvasPages = [...pages];
      state.isDirty = true;
    }
  }

  function renameCanvasPage(pageId: string, title: string): void {
    const pages = state.content.canvasPages;
    if (!pages) {
      return;
    }
    const page = pages.find((p) => p.id === pageId);
    if (!page) {
      return;
    }
    page.title = title.trim() || page.title;
    state.isDirty = true;
  }

  function switchToNextCanvasPage(): boolean {
    const pages = state.content.canvasPages;
    if (!pages?.length) {
      return false;
    }
    const activeId = state.content.activeCanvasPageId;
    const idx = pages.findIndex((p) => p.id === activeId);
    if (idx === -1 || idx >= pages.length - 1) {
      return false;
    }
    switchCanvasPage(pages[idx + 1].id);
    return true;
  }

  function switchToPreviousCanvasPage(): boolean {
    const pages = state.content.canvasPages;
    if (!pages?.length) {
      return false;
    }
    const activeId = state.content.activeCanvasPageId;
    const idx = pages.findIndex((p) => p.id === activeId);
    if (idx <= 0) {
      return false;
    }
    switchCanvasPage(pages[idx - 1].id);
    return true;
  }

  return {
    state: readonly(state),
    content,
    selectedBlock,
    isBlockLocked,
    setContent,
    selectBlock,
    setViewport,
    setDarkMode,
    setUiTheme,
    setPreviewMode,
    updateBlock,
    updateSettings,
    addBlock,
    removeBlock,
    moveBlock,
    markDirty,
    ensureCanvasPages,
    switchCanvasPage,
    addCanvasPage,
    removeCanvasPage,
    renameCanvasPage,
    switchToNextCanvasPage,
    switchToPreviousCanvasPage,
  };
}
